import React, { useEffect, useMemo, useState } from "react";
import {
  EuiPopover,
  EuiSelectable,
  EuiButtonEmpty,
  EuiFieldText,
  EuiFormControlLayout,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";

/**
 * Modern phone input: a country-code dropdown (flag + dial code, searchable)
 * joined to a national-number field. Rendered for OpenQuestions whose
 * TextValidator subType is PhoneNumber.
 *
 * The combined value is stored as `"<dial> <national>"` (e.g. "+49 151 1234567"),
 * which satisfies the anketa-core PhoneNumber validator (leading "+", digits and
 * common separators). An empty national number stores an empty string so a bare
 * dial code is never persisted.
 */

interface Country {
  code: string;
  name: string;
  dial: string;
  flag: string;
}

// Curated list (emoji flags); Germany first as the default market.
const COUNTRIES: Country[] = [
  { code: "DE", name: "Deutschland", dial: "+49", flag: "🇩🇪" },
  { code: "AT", name: "Österreich", dial: "+43", flag: "🇦🇹" },
  { code: "CH", name: "Schweiz", dial: "+41", flag: "🇨🇭" },
  { code: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
  { code: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { code: "FR", name: "France", dial: "+33", flag: "🇫🇷" },
  { code: "IT", name: "Italia", dial: "+39", flag: "🇮🇹" },
  { code: "ES", name: "España", dial: "+34", flag: "🇪🇸" },
  { code: "NL", name: "Nederland", dial: "+31", flag: "🇳🇱" },
  { code: "BE", name: "Belgique", dial: "+32", flag: "🇧🇪" },
  { code: "LU", name: "Luxembourg", dial: "+352", flag: "🇱🇺" },
  { code: "DK", name: "Danmark", dial: "+45", flag: "🇩🇰" },
  { code: "SE", name: "Sverige", dial: "+46", flag: "🇸🇪" },
  { code: "NO", name: "Norge", dial: "+47", flag: "🇳🇴" },
  { code: "FI", name: "Suomi", dial: "+358", flag: "🇫🇮" },
  { code: "PL", name: "Polska", dial: "+48", flag: "🇵🇱" },
  { code: "CZ", name: "Česko", dial: "+420", flag: "🇨🇿" },
  { code: "PT", name: "Portugal", dial: "+351", flag: "🇵🇹" },
  { code: "IE", name: "Ireland", dial: "+353", flag: "🇮🇪" },
  { code: "AU", name: "Australia", dial: "+61", flag: "🇦🇺" },
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦" },
];

const DEFAULT_COUNTRY = COUNTRIES[0];

// Longest dial codes first so "+49" is matched before "+4".
const DIAL_CODES_BY_LENGTH = [...COUNTRIES].sort(
  (a, b) => b.dial.length - a.dial.length,
);

const parseValue = (
  value: string,
): { country: Country; national: string } => {
  const trimmed = (value ?? "").trim();
  if (trimmed.startsWith("+")) {
    const match = DIAL_CODES_BY_LENGTH.find((c) => trimmed.startsWith(c.dial));
    if (match) {
      return {
        country: match,
        national: trimmed.slice(match.dial.length).trim(),
      };
    }
  }
  return { country: DEFAULT_COUNTRY, national: trimmed };
};

export const PhoneInput = ({
  value,
  onChange,
  onBlur,
  invalid,
  readOnly,
  placeholder,
}: {
  value: string;
  onChange: (val: string) => void;
  onBlur?: (val: string) => void;
  invalid?: boolean;
  readOnly?: boolean;
  placeholder?: string;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const initial = useMemo(() => parseValue(value), []);
  const [country, setCountry] = useState<Country>(initial.country);
  const [national, setNational] = useState<string>(initial.national);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Keep local state in sync when the stored value changes externally.
  useEffect(() => {
    const parsed = parseValue(value);
    setCountry(parsed.country);
    setNational(parsed.national);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const combine = (c: Country, n: string): string => {
    const num = n.trim();
    return num === "" ? "" : `${c.dial} ${num}`;
  };

  const options = COUNTRIES.map((c) => ({
    key: c.code,
    label: `${c.name} (${c.dial})`,
    prepend: <span style={{ fontSize: 16 }}>{c.flag}</span>,
    checked: c.code === country.code ? ("on" as const) : undefined,
    data: c,
  }));

  const countryButton = (
    <EuiButtonEmpty
      iconType="arrowDown"
      iconSide="right"
      color="text"
      isDisabled={readOnly}
      onClick={() => setIsOpen((o) => !o)}
      style={{ minWidth: 92 }}
    >
      <span style={{ fontSize: 16, marginRight: 6 }}>{country.flag}</span>
      {country.dial}
    </EuiButtonEmpty>
  );

  return (
    <EuiFormControlLayout
      readOnly={readOnly}
      fullWidth
      prepend={
        <EuiPopover
          button={countryButton}
          isOpen={isOpen}
          closePopover={() => setIsOpen(false)}
          panelPaddingSize="none"
          anchorPosition="downLeft"
        >
          <div style={{ width: 280 }}>
            <EuiSelectable
              searchable
              singleSelection
              searchProps={{
                placeholder: translate(
                  "anketa.questionnaire.editor.phone.country.search",
                  { defaultMessage: "Search country…" },
                ),
                compressed: true,
              }}
              options={options}
              onChange={(newOptions) => {
                const chosen = newOptions.find((o) => o.checked === "on");
                const next = (chosen?.data as Country) ?? country;
                setCountry(next);
                setIsOpen(false);
                onChange(combine(next, national));
              }}
              listProps={{ rowHeight: 36 }}
            >
              {(list, search) => (
                <>
                  <div style={{ padding: 8 }}>{search}</div>
                  {list}
                </>
              )}
            </EuiSelectable>
          </div>
        </EuiPopover>
      }
    >
      <EuiFieldText
        controlOnly
        type="tel"
        placeholder={placeholder || "151 23456789"}
        isInvalid={invalid}
        readOnly={readOnly}
        value={national}
        onChange={(e) => {
          setNational(e.target.value);
          onChange(combine(country, e.target.value));
        }}
        onBlur={(e) => onBlur?.(combine(country, e.target.value))}
      />
    </EuiFormControlLayout>
  );
};
